import React, { useState } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import styles from './LoginHero.module.less'

interface HeroSlide {
  key: string
  title: string
  subtitle: string
}

// Hero carousel for the /login right panel. Slides are static local content (not fetched
// from an API), and navigation is manual only — no autoplay, so it won't change slides out
// from under someone reading the page.
export const LoginHero = () => {
  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = useState(0)

  const slides: HeroSlide[] = [
    { key: 'slide1', title: t('loginPage.hero.slide1Title'), subtitle: t('loginPage.hero.slide1Subtitle') },
    { key: 'slide2', title: t('loginPage.hero.slide2Title'), subtitle: t('loginPage.hero.slide2Subtitle') },
    { key: 'slide3', title: t('loginPage.hero.slide3Title'), subtitle: t('loginPage.hero.slide3Subtitle') },
  ]

  const goTo = (index: number) => setActiveIndex((index + slides.length) % slides.length)

  return (
    <div className={styles.hero} role="group" aria-roledescription="carousel" aria-label="HICAS">
      {slides.map((slide, index) => (
        <div
          key={slide.key}
          className={classNames(styles.slide, styles[slide.key], {
            [styles.slideActive]: index === activeIndex,
          })}
          aria-hidden={index !== activeIndex}
        >
          <div className={styles.content}>
            <h2 className={styles.title}>{slide.title}</h2>
            <p className={styles.subtitle}>{slide.subtitle}</p>
            <span className={styles.link}>
              {t('loginPage.hero.learnMore')} <span aria-hidden="true">→</span>
            </span>
          </div>
        </div>
      ))}

      <button
        type="button"
        className={classNames(styles.arrow, styles.arrowPrev)}
        aria-label={t('loginPage.hero.previousSlide')}
        onClick={() => goTo(activeIndex - 1)}
      >
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M12.5 4.5 6 10l6.5 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <button
        type="button"
        className={classNames(styles.arrow, styles.arrowNext)}
        aria-label={t('loginPage.hero.nextSlide')}
        onClick={() => goTo(activeIndex + 1)}
      >
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M7.5 4.5 14 10l-6.5 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <div className={styles.dots}>
        {slides.map((slide, index) => (
          <button
            key={slide.key}
            type="button"
            className={styles.dot}
            aria-label={t('loginPage.hero.goToSlide', { index: index + 1 })}
            aria-current={index === activeIndex}
            onClick={() => goTo(index)}
          />
        ))}
      </div>
    </div>
  )
}
